import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Flame, Globe, MapPin } from 'lucide-react';
import { useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  getScopeProgress,
  type RatingScope,
  ratingScopes,
} from '@/data/countries';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface ScopeSelectorProps {
  currentScopeId: string;
  onScopeChange: (scopeId: string) => void;
  ratings: Record<string, number>;
  compact?: boolean;
  /** Optional progress override (e.g., for tournament mode) */
  progressOverride?: { rated: number; total: number; percentage: number };
}

/**
 * Scope option card component.
 */
function ScopeOption({
  scope,
  isSelected,
  progress,
  onSelect,
}: {
  scope: RatingScope;
  isSelected: boolean;
  progress: { rated: number; total: number; percentage: number };
  onSelect: () => void;
}) {
  return (
    <motion.button
      onClick={onSelect}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={cn(
        'w-full p-4 rounded-xl border-2 transition-all text-left',
        'flex items-center gap-4',
        isSelected
          ? 'border-primary bg-primary/5 shadow-md'
          : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50',
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          'w-12 h-12 rounded-xl flex items-center justify-center text-2xl',
          'bg-gradient-to-br from-muted to-muted/50',
          isSelected && 'from-primary/20 to-primary/10',
        )}
      >
        {scope.icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-base">{scope.name}</h3>
          {isSelected && <Check className="w-4 h-4 text-primary" />}
        </div>
        <p className="text-sm text-muted-foreground truncate">
          {scope.description}
        </p>
        <div className="flex items-center gap-2 mt-1.5">
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress.percentage}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className={cn(
                'h-full rounded-full',
                progress.percentage === 100 ? 'bg-green-500' : 'bg-primary',
              )}
            />
          </div>
          <span className="text-xs font-medium text-muted-foreground tabular-nums">
            {progress.rated}/{progress.total}
          </span>
        </div>
      </div>
    </motion.button>
  );
}

/**
 * Compact trigger button showing current scope.
 */
function ScopeTrigger({
  scope,
  progress,
  onClick,
  compact,
}: {
  scope: RatingScope;
  progress: { rated: number; total: number; percentage: number };
  onClick?: () => void;
  compact?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-xl',
        'bg-card border-2 border-border',
        'hover:border-primary/50 hover:bg-muted/50',
        'transition-all active:scale-[0.98]',
        'w-full sm:w-auto',
        compact ? 'px-2.5 py-1.5' : 'px-4 py-2.5 gap-3',
      )}
    >
      <span className={compact ? 'text-lg' : 'text-xl'}>{scope.icon}</span>
      <div className="flex-1 text-left min-w-0">
        <div className="flex items-center gap-1">
          <span
            className={cn(
              'font-semibold truncate',
              compact ? 'text-xs' : 'text-sm',
            )}
          >
            {scope.name}
          </span>
          <span
            className={cn(
              'text-muted-foreground shrink-0',
              compact ? 'text-[10px]' : 'text-xs',
            )}
          >
            ({progress.rated}/{progress.total})
          </span>
        </div>
        <div
          className={cn(
            'w-full bg-muted rounded-full overflow-hidden',
            compact ? 'h-0.5 mt-0.5' : 'h-1 mt-1',
          )}
        >
          <div
            className={cn(
              'h-full rounded-full transition-all duration-300',
              progress.percentage === 100 ? 'bg-green-500' : 'bg-primary',
            )}
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
      </div>
      <ChevronDown
        className={cn(
          'text-muted-foreground shrink-0',
          compact ? 'w-3 h-3' : 'w-4 h-4',
        )}
      />
    </button>
  );
}

/**
 * ScopeSelector - Mobile-first scope selection.
 *
 * On mobile: Uses a bottom sheet drawer for easy thumb access.
 * On desktop: Uses a dropdown-style drawer.
 */
export function ScopeSelector({
  currentScopeId,
  onScopeChange,
  ratings,
  compact,
  progressOverride,
}: ScopeSelectorProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const currentScope =
    ratingScopes.find((s) => s.id === currentScopeId) ?? ratingScopes[0];
  // Use override if provided (e.g., tournament mode), otherwise calculate from ratings
  const currentProgress = progressOverride ?? getScopeProgress(currentScopeId, ratings);

  const handleSelect = (scopeId: string) => {
    onScopeChange(scopeId);
    setOpen(false);
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <div>
          <ScopeTrigger
            scope={currentScope}
            progress={currentProgress}
            onClick={() => setOpen(true)}
            compact={compact}
          />
        </div>
      </DrawerTrigger>

      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="text-left pb-2">
          <DrawerTitle className="text-xl font-display flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            Choose Your Challenge
          </DrawerTitle>
          <p className="text-sm text-muted-foreground">
            Select a region to rate. Your ratings are saved across all regions.
          </p>
        </DrawerHeader>

        <div className="px-4 pb-6 space-y-3 overflow-y-auto">
          {ratingScopes.map((scope) => {
            const progress = getScopeProgress(scope.id, ratings);
            return (
              <ScopeOption
                key={scope.id}
                scope={scope}
                isSelected={scope.id === currentScopeId}
                progress={progress}
                onSelect={() => handleSelect(scope.id)}
              />
            );
          })}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
